import createContext from 'bz-utilities/hooks/context-v2';
import type { Constructable, DirtiedMap } from 'bz-utilities/ts';
import type { PropsWithChildren } from 'react';
import { useCallback } from 'react';
import type { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';

interface IFormUtils extends UseFormReturn<FieldValues, any, undefined> {
  onSetValue: (name: string, value: any) => void;
  autoSave: boolean;
}

export const [FormUtilsContext, useFormUtils] = createContext<IFormUtils>('FormUtilsContext');

export interface IProps extends PropsWithChildren {
  autoSave?: boolean;
}

export const FormUtilsProvider: React.FC<IProps> = ({ autoSave, children }) => {
  const { setValue, trigger, ...other } = useFormContext();

  const onSetValue = useCallback(
    (name: string, value: any) => {
      setValue(name, value, { shouldDirty: true });
      trigger(name);
    },
    [setValue, trigger]
  );

  const methods = {
    onSetValue,
    setValue,
    trigger,
    autoSave,
    ...other
  };

  return (
    <FormUtilsContext.Provider value={methods}>
      <>{children}</>
    </FormUtilsContext.Provider>
  );
};

export const createDtoFromDirtied = <T extends object>(
  dirtied: DirtiedMap<T>,
  next: T
): Partial<T> => {
  return Object.keys(dirtied).reduce(
    (acc, key) => {
      const _key = key as keyof T;
      acc[_key] = next[_key];

      return acc;
    },
    {} as Partial<T>
  );
};

export const useWatchValue = <T extends object, K extends keyof T>(
  clazz: Constructable<T>, // [chris hunter] - clazz is here for proper type saftey and inferencing DO NOT REMOVE
  key: K
): T[K] => {
  return useWatch<T>({ name: key }) as T[K];
};
